// Authentication and user-related type definitions
import type { User as SupabaseUser } from '@supabase/supabase-js';

export type UserTier = 'free' | 'premium';

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  tier: UserTier;
  emailVerified: boolean;
  createdAt?: Date;
  lastActive?: Date;
  maxGuitars?: number;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system';
  notifications?: boolean;
  autoSave?: boolean;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithPhone: (phone: string) => Promise<void>;
  verifyPhone: (phoneNumber: string, otp: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserTier: (tier: UserTier) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export interface LimitCheck {
  allowed: boolean;
  currentCount: number;
  limit: number;
  message?: string;
}

export interface FreeTierLimits {
  maxGuitarArrangements: number;
  allowedScales: string[];
  maxChordsInProgression: number;
  maxSavedPresets: number;
  allowedSoundTypes: string[];
  overlayFeatures: boolean;
  exportFeatures: boolean;
  cagedShapes: {
    maxActiveShapes: number;
    visualizations: {
      LINK: boolean;
    };
  };
}

// Convert Supabase User to our User type
export const mapSupabaseUser = (supabaseUser: SupabaseUser, profile?: any): User => {
  // All free users get 4 guitars, premium users get unlimited
  const tier = profile?.tier || 'free';
  const maxGuitars = tier === 'premium' ? Infinity : 4;
  
  return {
    uid: supabaseUser.id,
    email: supabaseUser.email || null,
    displayName: profile?.display_name || supabaseUser.user_metadata?.display_name || null,
    photoURL: profile?.avatar_url || supabaseUser.user_metadata?.avatar_url || null,
    tier,
    emailVerified: supabaseUser.email_confirmed_at ? true : false,
    createdAt: supabaseUser.created_at ? new Date(supabaseUser.created_at) : new Date(),
    lastActive: new Date(),
    maxGuitars
  };
};
